import { InterpolationMethod } from "../color-interpolator";
import { isEwaColorResampler } from "../color-resampler";
import type { ColorStringParser } from "../color-string-parser";
import { isImageAdapter } from "../image-adapter";
import {
  type VirtualPixelDecoratorApplicator,
  VirtualPixelMethod,
} from "../pixel-accessor";
import { isReversePixelMapper } from "../pixel-mapper";
import { isColor } from "../types";
import type {
  DistortionController,
  DistortionControllerOptions,
} from "./DistortionController";
import type {
  ReversePixelMapperResolver,
  GetReversePixelMapperFactoryArgs,
  ReversePixelMapperFactoriesPoolKeyMap,
  ReversePixelMapper,
} from "../pixel-mapper";
import type { ImageAdapter, ImageAdapterResolver } from "../image-adapter";
import type {
  ColorResamplerResolver,
  ColorResamplerResolverOptions,
  ColorResampler,
} from "../color-resampler";
import type { Color, Point } from "../types";

/**
 * Distortion service options.
 */
export type DistortionServiceOptions = DistortionControllerOptions &
  Omit<ColorResamplerResolverOptions, "matteColor"> & {
    /**
     * Matte color. Used for invalid pixel mappings, for which no real color can be calculated from source image.
     * @default [0, 0, 0, 0]
     */
    matteColor?: Color | string;

    /**
     * Image background color. May be used as virtual pixel color or interpolation color.
     *
     * @default [0, 0, 0, 0] (transparent).
     */
    imageBackgroundColor?: Color | string;

    /**
     * Source image viewport offset. May impact on pixel mapping calculation.
     */
    imageViewportOffset?: Point;
  };

/**
 * Distortion result interface.
 */
export interface DistortionResult<ResourceType> {
  /**
   * Distorted image.
   */
  image: ImageAdapter<ResourceType>;

  /**
   * Reverse pixel mapper instance, used for distortion.
   */
  distortion: ReversePixelMapper;

  /**
   * Distortion process start timestamp.
   */
  startTimestamp: number;

  /**
   * Distortion process end timestamp.
   */
  endTimestamp: number;

  /**
   * Distortion process duration (ms)
   */
  duration: number;

  /**
   * Color weight lookup table used in EWA resampler.
   */
  weightLookupTable?: number[];
}

/**
 * Distortion service class.
 */
export class DistortionService {
  /**
   * DistortionService constructor.
   *
   * @param reversePixelMapperResolver
   * @param colorResamplerResolver
   * @param distortionController
   * @param imageAdapterResolver
   * @param colorStringParser
   * @param virtualPixelDecoratorApplicator
   */
  constructor(
    private readonly reversePixelMapperResolver: ReversePixelMapperResolver,
    private readonly colorResamplerResolver: ColorResamplerResolver,
    private readonly distortionController: DistortionController,
    private readonly imageAdapterResolver: ImageAdapterResolver,
    private readonly colorStringParser: ColorStringParser,
    private readonly virtualPixelDecoratorApplicator: VirtualPixelDecoratorApplicator
  ) {}

  /**
   * Distorts image using distortion name and distortion arguments.
   *
   * @param image
   * @param distortion
   * @param args
   * @param options
   */
  distort<ResourceType, K extends keyof ReversePixelMapperFactoriesPoolKeyMap>(
    image: ResourceType | ImageAdapter<ResourceType>,
    distortion: K,
    args: GetReversePixelMapperFactoryArgs<K>,
    options?: DistortionServiceOptions
  ): Promise<DistortionResult<ResourceType>>;

  /**
   * Distorts image using provided ReversePixelMapper.
   *
   * @param image
   * @param reversePixelMapper
   * @param options
   */
  distort<ResourceType>(
    image: ResourceType | ImageAdapter<ResourceType>,
    reversePixelMapper: ReversePixelMapper,
    options?: DistortionServiceOptions
  ): Promise<DistortionResult<ResourceType>>;

  /**
   * Distorts image.
   *
   * @param image
   * @param distortionOrMapper
   * @param argsOrOptions
   * @param mayBeOptions
   */
  distort<ResourceType>(
    image: ResourceType | ImageAdapter<ResourceType>,
    distortionOrMapper:
      | keyof ReversePixelMapperFactoriesPoolKeyMap
      | string
      | ReversePixelMapper,
    argsOrOptions: number[] | DistortionServiceOptions | undefined,
    mayBeOptions?: DistortionServiceOptions
  ): Promise<DistortionResult<ResourceType>>;

  /**
   * distort method implementation.
   *
   * @param image
   * @param distortionOrMapper
   * @param argsOrOptions
   * @param mayBeOptions
   */
  async distort<ResourceType>(
    image: ResourceType | ImageAdapter<ResourceType>,
    distortionOrMapper:
      | keyof ReversePixelMapperFactoriesPoolKeyMap
      | string
      | ReversePixelMapper,
    argsOrOptions: number[] | DistortionServiceOptions | undefined,
    mayBeOptions?: DistortionServiceOptions
  ): Promise<DistortionResult<ResourceType>> {
    const [imageAdapter, reversePixelMapper, options] = this.clarifyArguments(
      image,
      distortionOrMapper,
      argsOrOptions,
      mayBeOptions
    );

    this.setImageAdapterOptions(imageAdapter, options);

    const colorResampler = this.getColorResampler(
      imageAdapter,
      reversePixelMapper,
      options
    );

    const startTimestamp = Date.now();
    const distortedImage = await this.distortionController.distort(
      imageAdapter,
      reversePixelMapper,
      colorResampler,
      options
    );
    const endTimestamp = Date.now();

    const weightLookupTable = isEwaColorResampler(colorResampler)
      ? colorResampler.getWeightLookupTable()
      : undefined;

    return {
      image: distortedImage,
      distortion: reversePixelMapper,
      startTimestamp,
      endTimestamp,
      duration: endTimestamp - startTimestamp,
      weightLookupTable,
    };
  }

  /**
   * Resolves source image adapter for distortion.
   *
   * @param image
   * @private
   */
  private resolveImageAdapter<ResourceType>(
    image: ResourceType | ImageAdapter<ResourceType>
  ): ImageAdapter<ResourceType> {
    if (isImageAdapter(image)) {
      return image;
    }
    return this.imageAdapterResolver.resolve(image);
  }

  /**
   * Clarifies ambiguous arguments.
   *
   * @param image
   * @param distortionOrMapper
   * @param argsOrOptions
   * @param mayBeOptions
   * @private
   */
  private clarifyArguments<ResourceType>(
    image: ResourceType | ImageAdapter<ResourceType>,
    distortionOrMapper:
      | keyof ReversePixelMapperFactoriesPoolKeyMap
      | string
      | ReversePixelMapper,
    argsOrOptions: number[] | DistortionServiceOptions | undefined,
    mayBeOptions?: DistortionServiceOptions
  ): [
    ImageAdapter<ResourceType>,
    ReversePixelMapper,
    DistortionServiceOptions
  ] {
    const imageAdapter = this.resolveImageAdapter(image);

    let reversePixelMapper: ReversePixelMapper;
    let options: DistortionServiceOptions | undefined;

    if (isReversePixelMapper(distortionOrMapper)) {
      if (Array.isArray(argsOrOptions)) {
        throw new TypeError(
          "Argument 3 should be options object or undefined when passing ReversePixelMapper as second argument"
        );
      }
      reversePixelMapper = distortionOrMapper;
      options = argsOrOptions;
    } else {
      if (!Array.isArray(argsOrOptions)) {
        throw new TypeError("Missing distortion arguments");
      }
      reversePixelMapper = this.reversePixelMapperResolver.resolve(
        distortionOrMapper,
        argsOrOptions,
        imageAdapter.getViewport()
      );
      options = mayBeOptions;
    }

    return [imageAdapter, reversePixelMapper, options || {}];
  }

  /**
   * Returns color resampler for image distortion.
   *
   * @param image
   * @param reversePixelMapper
   * @param options
   * @private
   */
  private getColorResampler<T>(
    image: ImageAdapter<T>,
    reversePixelMapper: ReversePixelMapper,
    options: DistortionServiceOptions
  ): ColorResampler {
    const {
      matteColor,
      filter = "RobidouxSharp",
      preferredResampler,
      virtualPixelMethod = VirtualPixelMethod.TRANSPARENT,
      interpolationMethod = InterpolationMethod.AVERAGE,
    } = options;

    const realMatteColor = this.resolveColor(
      matteColor,
      image.getQuantumRange()
    );

    // Apply virtual pixel decorator
    const virtualPixelDecoratedImage =
      this.virtualPixelDecoratorApplicator.decorate(image, virtualPixelMethod);

    return this.colorResamplerResolver.resolve(
      virtualPixelDecoratedImage,
      reversePixelMapper,
      {
        preferredResampler,
        matteColor: realMatteColor,
        filter,
        interpolationMethod,
        virtualPixelMethod,
      }
    );
  }

  /**
   * Sets image adapter options from provided distortion options.
   *
   * @param image
   * @param options
   * @private
   */
  private setImageAdapterOptions<T>(
    image: ImageAdapter<T>,
    options: DistortionServiceOptions
  ): void {
    const { imageBackgroundColor, imageViewportOffset } = options;

    image.setBackgroundColor(
      this.resolveColor(imageBackgroundColor, image.getQuantumRange())
    );
    if (imageViewportOffset) {
      image.getViewport().offset(...imageViewportOffset);
    }
  }

  /**
   * Resolves color from color options.
   *
   * @param color
   * @param quantumRange
   * @param defaultValue
   * @private
   */
  private resolveColor(
    color: Color | string | undefined,
    quantumRange: number,
    defaultValue: Color = [0, 0, 0, 0]
  ): Color {
    if (color === undefined) {
      return defaultValue;
    }
    if (isColor(color)) {
      return color;
    }
    const parsedColor = this.colorStringParser.parse(color, quantumRange);
    if (!parsedColor) {
      return defaultValue;
    }
    return parsedColor;
  }
}
